import { Box, Paper, Stack, Typography } from '@mui/material';
import { memo } from 'react';
import {
  ACTIVITY_TYPE_MAP,
  BADGE_SX,
  SURFACE_SX,
  TIMELINE_RAIL_SX,
  formatDateTime,
} from './customerDetailUtils';

const CustomerDetailTimelineSection = memo(function CustomerDetailTimelineSection({ items }) {
  if (items.length === 0) {
    return (
      <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 2, md: 2.25 } }}>
        <Stack spacing={0.75}>
          <Typography variant="h6" fontWeight={700}>
            Timeline
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No completed activities yet. Mark open work done or log a completed activity directly to build account history.
          </Typography>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 2, md: 2.25 } }}>
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Timeline
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Completed customer history, newest first.
          </Typography>
        </Box>

        <Box sx={TIMELINE_RAIL_SX}>
          <Stack spacing={1.35}>
            {items.map((item) => {
              if (item.type === 'separator') {
                return (
                  <Box key={item.key} sx={{ pl: 5 }}>
                    <Typography
                      variant="overline"
                      sx={{
                        px: 1,
                        py: 0.35,
                        borderRadius: '999px',
                        backgroundColor: '#eef7f6',
                        color: 'primary.main',
                        letterSpacing: 1.1,
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                );
              }

              const entry = item.entry;
              const activityType =
                ACTIVITY_TYPE_MAP.get(entry?.activityType) ?? ACTIVITY_TYPE_MAP.get('OTHER');
              const IconComponent = activityType.Icon;
              const linkedContact = entry?.contact ?? null;
              const authorName =
                String(entry?.completedBy?.displayName ?? entry?.createdBy?.displayName ?? '').trim() ||
                'Unknown user';
              const title = String(entry?.title ?? '').trim() || activityType.label;
              const detailText = String(entry?.note ?? '').trim();

              return (
                <Box
                  key={item.key}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '36px minmax(0, 1fr)',
                    gap: 1.35,
                    alignItems: 'flex-start',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: activityType.color,
                        backgroundColor: activityType.background,
                        border: `1px solid ${activityType.border}`,
                      }}
                    >
                      <IconComponent fontSize="small" />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      borderRadius: 3,
                      border: '1px solid rgba(0,0,0,0.06)',
                      backgroundColor: '#fcfdfd',
                      px: 1.35,
                      py: 1.2,
                    }}
                  >
                    <Stack spacing={0.85}>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                      >
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <Box
                            component="span"
                            sx={{
                              ...BADGE_SX,
                              color: activityType.color,
                              backgroundColor: activityType.background,
                              borderColor: activityType.border,
                            }}
                          >
                            {activityType.label}
                          </Box>
                          {linkedContact && (
                            <Box
                              component="span"
                              sx={{
                                ...BADGE_SX,
                                color: linkedContact.active ? '#0d47a1' : 'text.secondary',
                                backgroundColor: linkedContact.active
                                  ? 'rgba(13,71,161,0.08)'
                                  : 'rgba(0,0,0,0.04)',
                                borderColor: linkedContact.active
                                  ? 'rgba(13,71,161,0.16)'
                                  : 'rgba(0,0,0,0.08)',
                              }}
                            >
                              {linkedContact.name}
                              {!linkedContact.active ? ' (Archived)' : ''}
                              {linkedContact.isPrimary ? ' • Primary' : ''}
                            </Box>
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(entry?.occurredAt || entry?.completedAt || entry?.createdAt)}
                        </Typography>
                      </Stack>

                      <Box>
                        <Typography variant="subtitle2" fontWeight={700}>
                          {title}
                        </Typography>
                        {detailText && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.35, whiteSpace: 'pre-wrap' }}
                          >
                            {detailText}
                          </Typography>
                        )}
                      </Box>

                      <Typography variant="caption" color="text.secondary">
                        Logged by {authorName}
                      </Typography>
                    </Stack>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
});

export default CustomerDetailTimelineSection;
